/**
 * printExporter.ts
 * Turns rendered pages into a PDF blob (html2canvas/modern-screenshot + pdf-lib) or sends them to
 * the browser print dialog through a hidden iframe.
 *
 * Speed (profiled on TVS phiếu templates):
 * - html2canvas dominates wall time; cost ∝ width × height × scale².
 * - Capture at fixed paper CSS pixels (not unbounded scrollHeight) unless the
 *   page is flagged overflow — runaway height was the main multi-second tax.
 * - Default scale ≈1.25 (~120 DPI) is enough for office laser/inkjet text.
 */

import { PDFDocument, PDFPage, rgb } from "pdf-lib";
import type { CustomPaperMm, Orientation, PaperSize, RenderedPage } from "@/types/print";
import { PAPER_DIMENSIONS } from "@/types/print";

/** Paper geometry used when a rendered page does not carry its own size. */
export interface PaperSetup {
  paperSize: PaperSize;
  orientation: Orientation;
  /** Custom dimensions in mm (only meaningful when paperSize === "Custom"). */
  customMm?: CustomPaperMm;
}

export const DEFAULT_PAPER: PaperSetup = { paperSize: "A4", orientation: "portrait" };

/** JPEG quality for PDF embeds (0–1). */
export const PDF_JPEG_QUALITY = 0.86;

export function getPaperDimensionsMm(
  size: PaperSize,
  landscape?: boolean,
  customMm?: CustomPaperMm
): { width: number; height: number } {
  let dim = PAPER_DIMENSIONS[size] ?? PAPER_DIMENSIONS.A4;
  if (size === "Custom" && customMm && customMm.width > 0 && customMm.height > 0) {
    dim = { size, width: customMm.width, height: customMm.height, scale: 0.264583 };
  }
  if (landscape) return { width: dim.height, height: dim.width };
  return { width: dim.width, height: dim.height };
}

export function paperPx(
  size: PaperSize,
  landscape: boolean,
  customMm?: CustomPaperMm
): { width: number; height: number } {
  const mm = getPaperDimensionsMm(size, landscape, customMm);
  const mmToPx = 96 / 25.4;
  return {
    width: Math.round(mm.width * mmToPx),
    height: Math.round(mm.height * mmToPx),
  };
}

/**
 * Place a captured bitmap onto a PDF page without cropping: shrink uniformly
 * when the capture is taller (or wider) than the sheet, top-align, and center
 * horizontally. Used so A5 landscape export keeps every table row.
 */
export function fitContentToPageMm(
  contentWpx: number,
  contentHpx: number,
  pageWmm: number,
  pageHmm: number
): { x: number; y: number; w: number; h: number } {
  if (contentWpx <= 0 || contentHpx <= 0) {
    return { x: 0, y: 0, w: pageWmm, h: pageHmm };
  }
  const mmPerPx = pageWmm / contentWpx;
  let w = pageWmm;
  let h = contentHpx * mmPerPx;
  if (h > pageHmm) {
    const scale = pageHmm / h;
    w *= scale;
    h = pageHmm;
  }
  return { x: (pageWmm - w) / 2, y: 0, w, h };
}

/** Watermark text stamped at the bottom-left of every exported PDF page. */
// ASCII-safe (WinAnsi-encodable) so the built-in Helvetica font can render it
// without embedding a Vietnamese font file.
export const WATERMARK_TEXT = "BizBox.Asia - ERP & Document Printing";

/**
 * Draw a small, subtle brand watermark at the bottom-left corner of a PDF
 * page. Uses the built-in Helvetica font (no external font fetch), so it is
 * cheap and always available.
 */
export function drawWatermark(page: PDFPage): void {
  const fontSize = 7;
  const margin = 6; // pt from the bottom-left corner
  page.drawText(WATERMARK_TEXT, {
    x: margin,
    y: margin,
    size: fontSize,
    color: rgb(0.55, 0.55, 0.55),
    opacity: 0.6,
  });
}

/** Stamp a user-chosen paper size onto every page so PDF/print don't crop at the template size. */
export function withPaper(pages: RenderedPage[], paper: PaperSetup | null | undefined): RenderedPage[] {
  if (!paper) return pages;
  return pages.map((p) => ({
    ...p,
    paperSize: paper.paperSize,
    orientation: paper.orientation,
  }));
}

export interface GeneratePDFOptions {
  /** Fallback paper for pages rendered without a detected size. */
  paper?: PaperSetup;
  /**
   * Override capture scale. Prefer omit — auto-scale drops quality on large
   * batches so the tab does not OOM (pixels ∝ scale²).
   */
  renderScale?: number;
  /** When true, every page uses `paper` instead of its detected size. */
  forcePaper?: boolean;
  /** JPEG quality 0–1 (default PDF_JPEG_QUALITY). */
  jpegQuality?: number;
  /** Progress callback: completed page index (0-based) → total page count. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Capture scale for html2canvas.
 * 96 DPI × 1.25 ≈ 120 DPI — readable office print, ~40% the pixels of scale 2.
 */
export function resolvePdfRenderScale(pageCount: number, override?: number): number {
  if (override != null && Number.isFinite(override) && override > 0) {
    return override;
  }
  if (pageCount <= 0) return 1.25;
  if (pageCount > 80) return 1;
  if (pageCount > 30) return 1.1;
  if (pageCount > 8) return 1.15;
  return 1.25;
}

const HOST_STYLE_ID = "excel-uploader-pdf-capture-css";

const CAPTURE_FIXUP_CSS = `
.html2canvas-pdf-target {
  background: #ffffff !important;
  overflow: hidden !important;
  color: #000000 !important;
}
.html2canvas-pdf-target section.docx {
  overflow: hidden !important;
  box-shadow: none !important;
  margin: 0 !important;
  transform: none !important;
}
.html2canvas-pdf-target table {
  border-collapse: collapse !important;
}
.html2canvas-pdf-target td,
.html2canvas-pdf-target th {
  overflow: visible !important;
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  vertical-align: middle !important;
  text-align: center !important;
  padding: 5px 6px !important;
  line-height: 1.35 !important;
}
.html2canvas-pdf-target tr {
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
}
.html2canvas-pdf-target td p,
.html2canvas-pdf-target th p {
  margin: 0 !important;
  padding: 0 !important;
  min-height: auto !important;
  text-align: center !important;
}
.html2canvas-pdf-target p {
  min-height: auto !important;
}
`.trim();

function ensureHostCaptureCss(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(HOST_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = HOST_STYLE_ID;
  style.textContent = CAPTURE_FIXUP_CSS;
  document.head.appendChild(style);
}

/** Replace oklch() tokens html2canvas cannot paint (scoped, cheap). */
function scrubOklch(root: ParentNode): void {
  const styles = root.querySelectorAll("style");
  for (let i = 0; i < styles.length; i++) {
    const st = styles[i];
    const text = st.textContent;
    if (text && text.includes("oklch")) {
      st.textContent = text.replace(/oklch\([^)]+\)/gi, "#000000");
    }
  }
  const styled = root.querySelectorAll<HTMLElement>('[style*="oklch"]');
  for (let i = 0; i < styled.length; i++) {
    const el = styled[i];
    if (el.style?.cssText) {
      el.style.cssText = el.style.cssText.replace(/oklch\([^)]+\)/gi, "#000000");
    }
  }
}

function settleFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      return;
    }
    setTimeout(resolve, 16);
  });
}

async function waitFontsOnce(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    await document.fonts.ready;
  } catch {
    // ignore
  }
}

function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality: number): string {
  try {
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return canvas.toDataURL("image/png");
  }
}

/** Convert a canvas to raw JPEG bytes for pdf-lib `embedJpg`. */
function canvasToJpegBytes(canvas: HTMLCanvasElement, quality: number): Uint8Array {
  const dataUrl = canvasToJpegDataUrl(canvas, quality);
  const base64 = dataUrl.split(",")[1] ?? "";
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * src values that often break PDF capture / iframe print:
 * - blob: (docx-preview media URLs die outside the original document lifetime
 *   and are invisible to modern-screenshot foreignObject)
 * - http(s): may be CORS-tainted for canvas
 * data: URLs are already safe.
 */
export function isVolatileImageSrc(src: string | null | undefined): boolean {
  if (!src) return false;
  const s = src.trim();
  if (!s || s.startsWith("data:")) return false;
  return (
    s.startsWith("blob:") ||
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("//")
  );
}

/** Blob → data URL (browser FileReader, Node arrayBuffer fallback). */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
      reader.readAsDataURL(blob);
    });
  }
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  const b64 = btoa(binary);
  const type = blob.type || "application/octet-stream";
  return `data:${type};base64,${b64}`;
}

/** Fetch any same-origin / blob / http URL into a data URL. */
export async function urlToDataUrl(url: string): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

/** Prefer drawing a loaded <img> to canvas (works for same-origin + blob). */
async function imgElementToDataUrl(img: HTMLImageElement): Promise<string | null> {
  const src = img.currentSrc || img.getAttribute("src") || img.src || "";
  if (src.startsWith("data:")) return src;
  // Ensure decoded so naturalWidth is set.
  try {
    if (typeof img.decode === "function" && !img.complete) {
      await img.decode();
    }
  } catch {
    // decode can throw on broken images
  }
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    try {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        return c.toDataURL("image/png");
      }
    } catch {
      // tainted canvas
    }
  }
  if (src) return urlToDataUrl(src);
  return null;
}

function waitImgLoaded(img: HTMLImageElement, timeoutMs = 8000): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    img.addEventListener("load", finish, { once: true });
    img.addEventListener("error", finish, { once: true });
    setTimeout(finish, timeoutMs);
  });
}

/**
 * Rewrite volatile image sources inside a DOM subtree to durable data: URLs
 * so html2canvas / modern-screenshot / iframe print can paint logos & overlays.
 * Returns how many sources were rewritten.
 */
export async function inlineDomImages(root: ParentNode): Promise<number> {
  if (!root || typeof document === "undefined") return 0;
  let rewritten = 0;

  // 1) <img src|srcset>
  const imgs = Array.from(
    (root as ParentNode & { querySelectorAll?: typeof document.querySelectorAll }).querySelectorAll?.(
      "img"
    ) ?? []
  ) as HTMLImageElement[];

  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute("src") || img.src || "";
      const srcset = img.getAttribute("srcset") || "";
      const needsSrc = isVolatileImageSrc(src);
      const needsSrcset = /blob:|https?:/i.test(srcset);
      if (!needsSrc && !needsSrcset) return;

      const dataUrl = await imgElementToDataUrl(img);
      if (!dataUrl) return;
      img.setAttribute("src", dataUrl);
      img.removeAttribute("srcset");
      // Help capture engines that key off crossOrigin
      img.removeAttribute("crossorigin");
      img.crossOrigin = "anonymous";
      rewritten += 1;
      await waitImgLoaded(img);
    })
  );

  // 2) SVG <image href / xlink:href>
  const svgImages = Array.from(
    root.querySelectorAll?.("image") ?? []
  ) as SVGImageElement[];
  await Promise.all(
    svgImages.map(async (el) => {
      const href =
        el.getAttribute("href") ||
        el.getAttribute("xlink:href") ||
        el.href?.baseVal ||
        "";
      if (!isVolatileImageSrc(href)) return;
      const dataUrl = await urlToDataUrl(href);
      if (!dataUrl) return;
      el.setAttribute("href", dataUrl);
      el.setAttribute("xlink:href", dataUrl);
      rewritten += 1;
    })
  );

  // 3) CSS background-image: url(blob:...) / url(http...)
  const withBg = Array.from(
    root.querySelectorAll?.("[style*='background']") ?? []
  ) as HTMLElement[];
  await Promise.all(
    withBg.map(async (el) => {
      const bg = el.style?.backgroundImage || "";
      if (!bg || bg === "none") return;
      const match = /url\(\s*['"]?(blob:[^'")]+|https?:[^'")]+)['"]?\s*\)/i.exec(bg);
      if (!match) return;
      const dataUrl = await urlToDataUrl(match[1]);
      if (!dataUrl) return;
      el.style.backgroundImage = `url("${dataUrl}")`;
      rewritten += 1;
    })
  );

  // 4) Replace <canvas> snapshots (some renderers paint drawings onto canvas)
  const canvases = Array.from(root.querySelectorAll?.("canvas") ?? []) as HTMLCanvasElement[];
  for (const canvas of canvases) {
    if (canvas.width < 2 || canvas.height < 2) continue;
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const img = document.createElement("img");
      img.src = dataUrl;
      img.alt = canvas.getAttribute("aria-label") || "";
      img.style.cssText = canvas.style.cssText || "max-width:100%;height:auto;display:block;";
      const w = canvas.style.width || `${canvas.width}px`;
      const h = canvas.style.height || `${canvas.height}px`;
      img.style.width = w;
      img.style.height = h;
      canvas.parentNode?.replaceChild(img, canvas);
      rewritten += 1;
    } catch {
      // tainted canvas — leave as-is
    }
  }

  return rewritten;
}

/** Ensure a downloadable Blob has a usable MIME type (empty type breaks some browsers). */
export function ensureBlobType(blob: Blob, mime: string): Blob {
  if (blob.type && blob.type !== "application/octet-stream") return blob;
  return new Blob([blob], { type: mime });
}

export async function generatePDF(
  renderedPages: RenderedPage[],
  options: GeneratePDFOptions = {}
): Promise<Blob> {
  const count = renderedPages.length;
  const scale = resolvePdfRenderScale(count, options.renderScale);
  const jpegQuality =
    options.jpegQuality != null && options.jpegQuality > 0 && options.jpegQuality <= 1
      ? options.jpegQuality
      : PDF_JPEG_QUALITY;
  const fallback = options.paper ?? DEFAULT_PAPER;
  const forcePaper = Boolean(options.forcePaper && options.paper);
  if (renderedPages.length === 0) {
    return new Blob([""], { type: "application/pdf" });
  }

  const { default: html2canvas } = await import("html2canvas");
    // Prefer modern-screenshot (SVG foreignObject) — often much faster than html2canvas
    // on Chromium for DOCX-preview DOM. Fall back per-page if it returns empty.
    type DomToCanvas = (
      el: HTMLElement,
      opts?: Record<string, unknown>
    ) => Promise<HTMLCanvasElement>;
    let domToCanvas: DomToCanvas | null = null;
    try {
      const mod = await import("modern-screenshot");
      if (typeof mod.domToCanvas === "function") {
        domToCanvas = mod.domToCanvas as unknown as DomToCanvas;
      }
    } catch {
      domToCanvas = null;
    }
    ensureHostCaptureCss();
    await waitFontsOnce();
    await settleFrame();

    const pdf = await PDFDocument.create();

    const host = document.createElement("div");
    host.className = "html2canvas-pdf-target";
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
      "position:fixed;left:0;top:0;opacity:0.01;pointer-events:none;z-index:-1;background:#ffffff;";
    document.body.appendChild(host);

    try {
      for (let i = 0; i < renderedPages.length; i++) {
        const rendered = renderedPages[i];
        const pSize = forcePaper ? fallback.paperSize : rendered.paperSize ?? fallback.paperSize;
        const pLandscape = forcePaper
          ? fallback.orientation === "landscape"
          : (rendered.orientation ?? fallback.orientation) === "landscape";
        const pMm = getPaperDimensionsMm(pSize, pLandscape, fallback.customMm);
        const pPx = paperPx(pSize, pLandscape, fallback.customMm);

        host.style.width = `${pPx.width}px`;
        // Fixed paper box — capture cost tracks this rectangle, not content scrollHeight.
        const captureH = rendered.overflow
          ? Math.min(Math.max(pPx.height, Math.round(pPx.height * 1.5)), pPx.height * 2)
          : pPx.height;
        host.style.height = `${captureH}px`;
        host.style.overflow = "hidden";
        host.innerHTML = rendered.html;

        const section = host.querySelector("section.docx") as HTMLElement | null;
        if (section) {
          section.style.width = `${pPx.width}px`;
          section.style.height = `${captureH}px`;
          section.style.maxHeight = `${captureH}px`;
          section.style.overflow = "hidden";
          section.style.boxShadow = "none";
          section.style.margin = "0";
          section.style.transform = "none";
        }
        scrubOklch(host);
        // Logos / overlay images from docx-preview are blob: URLs — inline before capture.
        await inlineDomImages(host);
        await settleFrame();

        if (i === 0) await settleFrame();

        const target = section ?? host;
        let canvas: HTMLCanvasElement | null = null;

        if (domToCanvas) {
          try {
            canvas = await domToCanvas(target, {
              width: pPx.width,
              height: captureH,
              scale,
              backgroundColor: "#ffffff",
              quality: jpegQuality,
              // Avoid fetching external fonts again (we waited fonts.ready once).
              // Resolve blob:/relative assets from the inlined tree when possible.
              fetchFn: async (url: string) => {
                if (/\.(woff2?|ttf|otf)(\?|$)/i.test(url)) {
                  return new Response(new ArrayBuffer(0));
                }
                if (url.startsWith("data:")) {
                  // modern-screenshot may still ask for data URLs via fetch
                  const comma = url.indexOf(",");
                  const meta = url.slice(0, comma);
                  const payload = url.slice(comma + 1);
                  const isB64 = /;base64/i.test(meta);
                  const bytes = isB64
                    ? Uint8Array.from(atob(payload), (c) => c.charCodeAt(0))
                    : new TextEncoder().encode(decodeURIComponent(payload));
                  const mime = meta.slice(5).split(";")[0] || "application/octet-stream";
                  return new Response(bytes, { headers: { "Content-Type": mime } });
                }
                try {
                  return await fetch(url);
                } catch {
                  return new Response(new ArrayBuffer(0));
                }
              },
            });
            if (!canvas || canvas.width < 8 || canvas.height < 8) {
              canvas = null;
            }
          } catch {
            canvas = null;
          }
        }

        if (!canvas) {
          canvas = await html2canvas(target, {
            width: pPx.width,
            height: captureH,
            scale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            logging: false,
            imageTimeout: 15000,
            removeContainer: true,
            foreignObjectRendering: false,
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
            windowWidth: pPx.width,
            windowHeight: captureH,
          });
        }

        const mmToPt = 72 / 25.4;
        const page = pdf.addPage([pMm.width * mmToPt, pMm.height * mmToPt]);
        const box = fitContentToPageMm(canvas.width, canvas.height, pMm.width, pMm.height);
        const jpg = await pdf.embedJpg(canvasToJpegBytes(canvas, jpegQuality));
        page.drawImage(jpg, {
          x: box.x * mmToPt,
          y: box.y * mmToPt,
          width: box.w * mmToPt,
          height: box.h * mmToPt,
        });
        // Watermark: small BizBox.Asia brand text at the bottom-left corner.
        drawWatermark(page);
        canvas.width = 0;
        canvas.height = 0;

        if (i > 0 && i % 5 === 0) {
          await new Promise<void>((r) => setTimeout(r, 0));
        }
        options.onProgress?.(i + 1, count);
      }
    } finally {
      if (host.parentNode) host.parentNode.removeChild(host);
    }

    const bytes = await pdf.save();
    // Always set application/pdf — empty type breaks download managers / some viewers.
    return new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  }

export async function printDocument(
  renderedPages: RenderedPage[],
  paper: PaperSetup = DEFAULT_PAPER
): Promise<void> {
  if (renderedPages.length === 0) return;
  const first = renderedPages[0];
  const size = first.paperSize ?? paper.paperSize;
  const isLandscape = (first.orientation ?? paper.orientation) === "landscape";
  const mm = getPaperDimensionsMm(size, isLandscape, paper.customMm);
  const orientStr = isLandscape ? "landscape" : "portrait";

  // Pre-inline images in an offscreen host so the print iframe never sees blob: URLs.
  const prep = document.createElement("div");
  prep.style.cssText = "position:fixed;left:-10000px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;";
  document.body.appendChild(prep);
  let pagesHtml: string[] = [];
  try {
    for (const p of renderedPages) {
      prep.innerHTML = p.html;
      scrubOklch(prep);
      await inlineDomImages(prep);
      const section = prep.querySelector("section.docx");
      pagesHtml.push(section ? section.outerHTML : prep.innerHTML);
    }
  } finally {
    if (prep.parentNode) prep.parentNode.removeChild(prep);
  }

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = Math.round((mm.width * 96) / 25.4) + "px";
  iframe.style.height = Math.round((mm.height * 96) / 25.4) + "px";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      try {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      } catch {
        // ignore
      }
    }, 1000);
  };

  try {
    const cd = iframe.contentDocument;
    const cw = iframe.contentWindow;
    if (!cd || !cw) return;

    cd.open();
    cd.write(
      "<!DOCTYPE html><html><head><title>Print</title><style>" +
        "@page{size:" +
        mm.width +
        "mm " +
        mm.height +
        "mm " +
        orientStr +
        ";margin:0;}" +
        "html,body{margin:0;padding:0;background:#ffffff;color:#000000;-webkit-print-color-adjust:exact;print-color-adjust:exact;}" +
        ".page{page-break-after:always;page-break-inside:avoid;width:" +
        mm.width +
        "mm;height:" +
        mm.height +
        "mm;overflow:hidden;position:relative;background:#ffffff;box-sizing:border-box;margin:0;padding:0;}" +
        ".page:last-child{page-break-after:avoid;}" +
        ".page section.docx { overflow: visible !important; height: auto !important; max-height: none !important; box-shadow: none !important; margin: 0 !important; }" +
        ".page .print-fit { transform-origin: top center; width: 100%; }" +
        ".page table { border-collapse: collapse !important; }" +
        ".page td, .page th { overflow: visible !important; height: auto !important; line-height: 1.45 !important; vertical-align: middle !important; padding: 6px 8px !important; }" +
        ".page tr { height: auto !important; }" +
        ".page p { min-height: auto !important; line-height: 1.45 !important; }" +
        ".page td p, .page th p { margin: 0 !important; padding: 2px 0 !important; }" +
        ".page img, .page image { max-width: 100%; height: auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }" +
        ".page .watermark { position: absolute; left: 6mm; bottom: 4mm; font-size: 7pt; color: #8a8a8a; opacity: 0.6; }" +
        "@media print { html, body, .page { opacity: 1 !important; visibility: visible !important; display: block !important; } }" +
        "</style></head><body>" +
        pagesHtml
          .map(function (html) {
            return (
              '<div class="page"><div class="print-fit">' +
              html +
              '</div><span class="watermark">' +
              WATERMARK_TEXT +
              "</span></div>"
            );
          })
          .join("") +
        "</body></html>"
    );
    cd.close();

    // Double-check images inside the iframe (in case any blob slipped through).
    await inlineDomImages(cd);
    await settleFrame();
    await new Promise(function (r) {
      setTimeout(r, 120);
    });

    Array.from(cd.querySelectorAll(".page")).forEach((pageEl) => {
      const page = pageEl as HTMLElement;
      const fit = page.querySelector(".print-fit") as HTMLElement | null;
      if (!fit) return;
      const pageH = page.clientHeight;
      const contentH = fit.scrollHeight;
      if (pageH > 0 && contentH > pageH + 2) {
        fit.style.transform = "scale(" + pageH / contentH + ")";
      }
    });

    cw.onafterprint = cleanup;
    cw.focus();
    cw.print();
    setTimeout(cleanup, 60000);
  } catch (err) {
    cleanup();
    throw err;
  }
}
